import { array } from 'fp-ts';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';
import {
    AccountingExportIntegrationAccountingConfiguration,
    AccountingExportIntegrationAccountingExportUpdateParameters,
    AccountingExportIntegrationExportTypes,
    AccountingExportIntegrationTaxAccountMapping,
} from '@mews-commander/data-access';
import { ModalWrapper, NewModalBodyWrapper, OverlayedLoader } from '@mews-commander/ui';
import { createZodFormResolver, getDefaults, logSentryError, lt, Form } from '@mews/framework';
import { Button, Card, CardContent, Grid, GridItem, Stack } from '@optimus-web/core';
import { useCloseButton } from '../../../../core/windowManager/hooks';
import { useCurrentEnterpriseTaxCodes } from '../../hooks';
import ModalHeader from '../../../../legacy/React/Components/Common/ModalHeader';
import {
    useExportAccountingConfigurationGet,
    useExportAccountingConfigurationUpdate,
} from '../queries/AccountingExportQuery';
import { ConfigurationDetailForm } from './ConfigurationDetailForm';
import { FieldNames } from './fieldNames';
import { accountingExportConfigurationSchema, AccountingExportConfigurationSchema } from './formProps';
import { ExportForm } from './export/exportForm';

const resolver = createZodFormResolver<AccountingExportConfigurationSchema>(accountingExportConfigurationSchema);

const ConfigurationDetailFormContainer = ({ integrationId }: { integrationId: string }) => {
    const { data, isLoading } = useExportAccountingConfigurationGet({ integrationId });
    const { mutateAsync, isPending: isSaving } = useExportAccountingConfigurationUpdate();
    const currentEnterpriseTaxCodes = useCurrentEnterpriseTaxCodes();
    const dispatch = useDispatch();

    const currentEnterpriseTaxAccountMappings: Array<AccountingExportIntegrationTaxAccountMapping> = useMemo(
        () =>
            currentEnterpriseTaxCodes && !array.isEmpty(currentEnterpriseTaxCodes)
                ? currentEnterpriseTaxCodes.map((taxCode) => ({
                      [FieldNames.taxCode]: taxCode,
                      [FieldNames.exportType]: AccountingExportIntegrationExportTypes.Journal,
                      [FieldNames.externalCode]: '',
                  }))
                : [],
        [currentEnterpriseTaxCodes]
    );

    const accountingExportConfiguration: AccountingExportIntegrationAccountingConfiguration | undefined =
        useMemo(() => {
            const configuration = data?.configuration ? { ...data.configuration } : undefined;

            if (!configuration) {
                return configuration;
            }

            const taxAccountMappingsConfigured = configuration?.taxAccountMappings
                ? configuration?.taxAccountMappings
                : [];

            /** Below logic adapts the tax account mappings configuration with the latest legal tax environment */
            const taxAccountMappings = currentEnterpriseTaxAccountMappings.map((item) => {
                const configuredMapping = taxAccountMappingsConfigured.find(
                    (mapping) => mapping.taxCode === item.taxCode
                );
                if (configuredMapping) {
                    return configuredMapping;
                }
                return item;
            });

            return {
                ...configuration,
                taxAccountMappings,
            } satisfies AccountingExportIntegrationAccountingConfiguration;
        }, [data?.configuration, currentEnterpriseTaxAccountMappings]);

    const accountingExportConfigurationDefaultValues: AccountingExportIntegrationAccountingConfiguration = useMemo(
        () => ({
            ...getDefaults(accountingExportConfigurationSchema),
            taxAccountMappings: currentEnterpriseTaxAccountMappings,
        }),
        [currentEnterpriseTaxAccountMappings]
    );

    const handleSubmit = async (formData: AccountingExportIntegrationAccountingConfiguration) => {
        try {
            const updateParams: AccountingExportIntegrationAccountingExportUpdateParameters = {
                id: integrationId,
                configuration: { ...formData },
            };
            await mutateAsync(updateParams);
        } catch (error) {
            dispatch(logSentryError({ error }));
        }
    };

    return (
        <>
            <OverlayedLoader show={isLoading || isSaving} />
            {!isLoading && data ? (
                <Grid
                    columnGap="200"
                    templateColumns="minmax(0, 4fr) minmax(0, 2fr)"
                    height="100%"
                    tabletOnly={{
                        templateColumns: 'minmax(0, 4fr)',
                    }}
                    largePhoneOnly={{
                        templateColumns: 'minmax(0, 2fr)',
                    }}
                    phoneOnly={{
                        templateColumns: 'minmax(0, 1fr)',
                    }}
                >
                    <GridItem>
                        <Form
                            onSubmit={handleSubmit}
                            resolver={resolver}
                            defaultValues={accountingExportConfigurationDefaultValues}
                            values={accountingExportConfiguration}
                            resetOptions={{ keepDefaultValues: true, keepDirty: true }}
                        >
                            {({ submit }) => (
                                <Card padding="200">
                                    <CardContent padding="200">
                                        <Stack vertical spacing="300">
                                            <ConfigurationDetailForm
                                                accountingSystemType={data.configuration?.accountingSystemType}
                                            />
                                            <Stack spacing="300" alignment="end">
                                                <Button variant="primary" onClick={submit} type="submit">
                                                    {lt('SaveChanges')}
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )}
                        </Form>
                    </GridItem>
                    <GridItem>
                        <ExportForm integrationId={integrationId} />
                    </GridItem>
                </Grid>
            ) : null}
        </>
    );
};

export const ConfigurationDetailFormWrapper = ({ integrationId }: { integrationId: string }) => {
    const closeButton = useCloseButton();

    return (
        <ModalWrapper>
            <ModalHeader title={lt('Configuration')} primaryAction={closeButton} />
            <NewModalBodyWrapper padded>
                <ConfigurationDetailFormContainer integrationId={integrationId} />
            </NewModalBodyWrapper>
        </ModalWrapper>
    );
};
